import { isNearExpiry } from '@/lib/jwt';
import { reauthGate } from '@/lib/reauth-gate';
import { useAuthStore } from '@/stores/auth';
import axios, { type AxiosError, type AxiosResponse } from 'axios';

export const API_BASE = '/api/v1';

// Every request has to give up eventually. Without a timeout a wedged socket
// leaves the request — and the react-query entry waiting on it — pending
// forever, which the UI renders as a spinner that never resolves.
const REQUEST_TIMEOUT_MS = 30_000;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
});

/**
 * Endpoints that must never take the proactive-refresh path.
 *
 * `/auth/refresh` is issued through this same client, so refreshing from
 * inside its own request interceptor awaits the in-flight promise that the
 * call is itself still waiting on — a circular await that hangs before the
 * request is ever dispatched. (`performRefresh` single-flights, so the nested
 * call gets handed back the very promise it is blocking.)
 */
function isAuthEndpointUrl(url: string | undefined): boolean {
  return typeof url === 'string' && /\/auth\/(refresh|login|logout)(\/|$|\?)/.test(url);
}

let refreshHandler: (() => Promise<boolean>) | null = null;
export function setRefreshHandler(handler: () => Promise<boolean>) {
  refreshHandler = handler;
}

api.interceptors.request.use(async (cfg) => {
  let token = useAuthStore.getState().access;
  if (token && isNearExpiry(token, 30) && refreshHandler && !isAuthEndpointUrl(cfg.url)) {
    await refreshHandler();
    token = useAuthStore.getState().access;
  }
  if (token) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

type RetryCfg = { url?: string; _retried?: boolean } & Record<string, unknown>;

export async function handleResponseError(
  err: AxiosError<{ error?: { code?: string } }>,
  retry: (cfg: RetryCfg) => Promise<AxiosResponse> = (c) => api.request(c),
): Promise<AxiosResponse> {
  const status = err.response?.status;
  const code = err.response?.data?.error?.code;
  const cfg = err.config as RetryCfg | undefined;

  if (status === 401 && cfg && !cfg._retried && !isAuthEndpointUrl(cfg.url)) {
    if (useAuthStore.getState().reauthRequired) {
      await reauthGate();
      cfg._retried = true;
      return retry(cfg);
    }
    if (code === 'ERR_AUTH_TOKEN_INVALID') {
      if (!refreshHandler)
        console.warn('[api] 401 with no refresh handler registered; cannot refresh token');
      const ok = refreshHandler ? await refreshHandler() : false;
      cfg._retried = true;
      if (!ok) await reauthGate();
      return retry(cfg);
    }
  }
  throw err;
}

api.interceptors.response.use(
  (res) => res,
  (err) => handleResponseError(err),
);
